import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAdvertiser } from "./useAdvertiser";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function TwoFactorPage() {
  const [params] = useSearchParams();
  const mode = params.get("mode") === "setup" ? "setup" : "login";
  useSeo({ title: mode === "setup" ? "Set up two-factor authentication | LUX" : "Enter authenticator code | LUX", ...PRIVATE_SEO });
  const navigate = useNavigate();
  const { session, refetchSession, invalidate, needs2faSetup, isAuthenticated, isPending } = useAdvertiser();
  const [setupInfo, setSetupInfo] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const requested = useRef(false);

  const resolvedMode = mode === "setup" || needs2faSetup ? "setup" : "login";

  const startSetup = useCallback(async () => {
    if (requested.current) return;
    requested.current = true;
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupInfo(data);
    } catch (err) {
      setError(errMsg(err));
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    if (!isPending && !isAuthenticated) {
      navigate("/advertiser/login", { replace: true });
      return;
    }
    if (resolvedMode === "setup" && !setupInfo) {
      startSetup();
    }
  }, [session, isAuthenticated, isPending, resolvedMode, setupInfo, startSetup, navigate]);

  const submitRecovery = async (e) => {
    e?.preventDefault?.();
    setError("");
    const clean = recoveryCode.trim();
    if (clean.length < 8) {
      setError("Enter one of your recovery codes (XXXXX-XXXXX)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/recovery", { code: clean });
      toast.success("Signed in with a recovery code", {
        description: `${data.recovery_codes_left} code(s) left. Set up a new authenticator in Security.`,
        duration: 8000,
      });
      invalidate();
      await refetchSession();
      navigate("/advertiser/security", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator");
      return;
    }
    setBusy(true);
    try {
      if (resolvedMode === "setup") {
        await api.post("/auth/2fa/enable", { code });
        toast.success("Two-factor authentication enabled");
      } else {
        await api.post("/auth/login/totp", { code });
        toast.success("Signed in");
      }
      invalidate();
      await refetchSession();
      navigate("/advertiser", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (!setupInfo?.secret) return;
    try {
      navigator.clipboard.writeText(setupInfo.secret);
      toast.success("Secret copied");
    } catch (_) {
      toast.error("Copy not supported on this device");
    }
  };

  if (resolvedMode === "setup") {
    return (
      <section className="lux-card p-6 sm:p-8" data-testid="twofa-setup-page">
        <p className="lux-label">Secure your account</p>
        <h1 className="mt-3 font-serif text-3xl text-lux-text">Set up two-factor authentication</h1>
        <p className="mt-2 text-sm text-lux-muted">
          Scan the QR with Google Authenticator, Authy or 1Password, then enter the 6-digit code the app shows. 2FA is required on every sign-in.
        </p>
        {setupInfo ? (
          <div className="mt-6 space-y-6">
            <div className="grid gap-4 sm:grid-cols-[180px_1fr]" data-testid="twofa-setup-qr-section">
              <div className="flex items-center justify-center rounded-[12px] border border-lux-border bg-white p-3" data-testid="auth-totp-qr">
                <QRCodeCanvas value={setupInfo.otpauth_uri} size={160} level="M" includeMargin={false} />
              </div>
              <div>
                <p className="text-xs text-lux-muted">Can't scan? Type this secret into your app:</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 break-all rounded-[8px] border border-lux-border bg-lux-surface2 px-3 py-2 font-mono text-[13px] text-lux-silverBright" data-testid="twofa-setup-secret">
                    {setupInfo.secret}
                  </code>
                  <button onClick={copySecret} className="lux-btn lux-btn-secondary lux-btn-sm" aria-label="Copy secret" data-testid="twofa-setup-copy-secret">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-3 inline-flex items-center gap-2 text-xs text-lux-muted">
                  <ShieldCheck className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" />
                  Keep this secret private. Never share your 6-digit code.
                </p>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <label htmlFor="totp-setup" className="lux-label">Enter the 6-digit code</label>
              <InputOTP maxLength={6} value={code} onChange={setCode} containerClassName="gap-2" data-testid="auth-totp-code-input">
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-12 border-lux-border bg-lux-surface2 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {error && <p role="alert" className="text-sm text-lux-error" data-testid="twofa-error">{error}</p>}
              <button type="submit" disabled={busy || code.length !== 6} className="lux-btn lux-btn-primary w-full" data-testid="auth-totp-submit-button">
                {busy ? "Verifying..." : "Enable 2FA"}
              </button>
            </form>
          </div>
        ) : (
          <p className="mt-6 text-sm text-lux-muted">Preparing your 2FA details...</p>
        )}
      </section>
    );
  }

  if (useRecovery) {
    return (
      <section className="lux-card p-6 sm:p-8" data-testid="twofa-recovery-page">
        <p className="lux-label">Second step</p>
        <h1 className="mt-3 font-serif text-3xl text-lux-text">Use a recovery code</h1>
        <p className="mt-2 text-sm text-lux-muted">Type one of the recovery codes you saved when you set up your authenticator. Each code works once.</p>
        <form onSubmit={submitRecovery} className="mt-6 space-y-5">
          <input
            className="lux-input text-center font-mono text-lg uppercase tracking-[0.2em]"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            placeholder="XXXXX-XXXXX"
            autoComplete="one-time-code"
            autoFocus
            spellCheck={false}
            data-testid="auth-recovery-code-input"
          />
          {error && <p role="alert" className="text-sm text-lux-error" data-testid="twofa-error">{error}</p>}
          <button type="submit" disabled={busy || recoveryCode.trim().length < 8} className="lux-btn lux-btn-primary w-full" data-testid="auth-recovery-submit-button">
            {busy ? "Verifying..." : "Sign in with recovery code"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-lux-muted">
          <button type="button" onClick={() => { setUseRecovery(false); setError(""); }} className="lux-link" data-testid="auth-use-authenticator-link">Back to authenticator code</button>
        </p>
      </section>
    );
  }

  return (
    <section className="lux-card p-6 sm:p-8" data-testid="twofa-login-page">
      <p className="lux-label">Second step</p>
      <h1 className="mt-3 font-serif text-3xl text-lux-text">Enter your authenticator code</h1>
      <p className="mt-2 text-sm text-lux-muted">Open Google Authenticator, Authy or your authenticator app and type the current 6-digit code.</p>
      <form onSubmit={submit} className="mt-6 space-y-5">
        <InputOTP maxLength={6} value={code} onChange={setCode} containerClassName="gap-2 justify-center" data-testid="auth-totp-code-input" autoFocus>
          <InputOTPGroup>
            {Array.from({ length: 6 }).map((_, i) => (
              <InputOTPSlot key={i} index={i} className="h-14 w-12 border-lux-border bg-lux-surface2 text-xl" />
            ))}
          </InputOTPGroup>
        </InputOTP>
        {error && <p role="alert" className="text-sm text-lux-error" data-testid="twofa-error">{error}</p>}
        <button type="submit" disabled={busy || code.length !== 6} className="lux-btn lux-btn-primary w-full" data-testid="auth-totp-submit-button">
          {busy ? "Verifying..." : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-lux-muted">
        Lost your phone?{" "}
        <button type="button" onClick={() => { setUseRecovery(true); setError(""); }} className="lux-link" data-testid="auth-use-recovery-link">Use a recovery code</button>
        {" "}or <Link to="/report" className="lux-link">contact support</Link>
      </p>
    </section>
  );
}
